import React, { useEffect, useState } from "react";
import { TrendingUp, Check, X, HelpCircle, PoundSterling, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";

/**
 * BookingConversionCard — admin-only card surfacing click → booking
 * attribution from both feedback channels (user self-report + partner
 * self-report). Sits inside the Admin dashboard so the founder finally
 * gets a real, honest answer to "what % of clicks become bookings?"
 */
export default function BookingConversionCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/booking-conversion")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white border hairline p-6 mb-8" data-testid="booking-conversion-loading">
        <div className="eyebrow mb-2">Click → Booking Conversion</div>
        <div className="text-sm text-[#666]">Crunching the numbers…</div>
      </div>
    );
  }

  if (!data) return null;

  const sr = data.self_report || {};
  const pc = data.partner_channel || {};

  return (
    <div className="bg-white border hairline p-6 mb-8" data-testid="booking-conversion-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="eyebrow flex items-center gap-2 mb-1">
            <TrendingUp className="w-3 h-3" /> Click → Booking Conversion
          </div>
          <div className="font-display text-2xl">Are our clicks turning into bookings?</div>
        </div>
      </div>

      {/* SELF-REPORT — user-side */}
      <div className="mb-6">
        <div className="text-xs mono uppercase tracking-[0.18em] text-[#666] font-bold mb-3">
          Channel A · User self-report
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-0 border hairline">
          <Cell label="Prompts shown" value={sr.promptable_clicks ?? 0} hint="Clicks made by logged-in users 48h+ ago" />
          <Cell label="Answered" value={sr.answered ?? 0} hint={`${sr.response_rate_pct ?? 0}% response rate`} border />
          <Cell label="Yes" value={sr.yes ?? 0} icon={<Check className="w-3.5 h-3.5 text-[#16a34a]" />} border />
          <Cell label="No" value={sr.no ?? 0} icon={<X className="w-3.5 h-3.5 text-[#666]" />} border />
          <Cell
            label="Conversion"
            value={`${sr.conversion_rate_pct ?? 0}%`}
            hint="of clicks that became bookings"
            border
            accent
          />
        </div>
        {sr.self_reported_value_gbp > 0 && (
          <div className="mt-3 text-sm text-[#444] flex items-center gap-1.5">
            <PoundSterling className="w-3.5 h-3.5 text-[#16a34a]" />
            <strong>£{sr.self_reported_value_gbp.toLocaleString()}</strong>
            <span className="text-[#666]">of self-reported booking value attributed to your clicks</span>
          </div>
        )}
      </div>

      {/* PARTNER CHANNEL */}
      <div>
        <div className="text-xs mono uppercase tracking-[0.18em] text-[#666] font-bold mb-3">
          Channel B · Partner self-report (Frowen, Orcaber, etc.)
        </div>
        <div className="grid grid-cols-3 gap-0 border hairline">
          <Cell label="All-time bookings" value={pc.total_bookings ?? 0} />
          <Cell label="Last 30 days" value={pc.bookings_last_30d ?? 0} border />
          <Cell
            label="All-time value"
            value={`£${(pc.total_value_gbp ?? 0).toLocaleString()}`}
            border
            accent
          />
        </div>

        {Array.isArray(pc.recent) && pc.recent.length > 0 && (
          <div className="mt-4">
            <div className="text-xs text-[#666] mb-2">Most recent partner-reported bookings:</div>
            <div className="space-y-1.5">
              {pc.recent.map((r) => (
                <div key={r.report_id} className="flex items-center justify-between text-sm border hairline px-3 py-2 bg-[#fafafa]">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{r.partner_slug}</div>
                    <div className="text-xs text-[#666] truncate">
                      Ref {r.booking_ref}{r.notes ? ` · ${r.notes}` : ""}
                    </div>
                  </div>
                  <div className="text-right ml-3 flex-shrink-0">
                    <div className="mono font-bold">£{(r.booking_value_gbp || 0).toLocaleString()}</div>
                    <div className="text-[10px] text-[#999]">{(r.created_at || "").slice(0, 10)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <details className="mt-4 text-xs">
          <summary className="cursor-pointer text-[#ff4500] hover:text-[#0e0e0e] font-bold uppercase tracking-wider">
            How to get a partner&apos;s reporting link
          </summary>
          <div className="mt-2 p-3 bg-[#fff8ec] border-l-2 border-[#fbbf24] text-[#444] leading-relaxed">
            Hit <code className="mono bg-white px-1 py-0.5">GET /api/partner-booking-key/&lt;partner-slug&gt;</code> from a tool like Postman.
            E.g. <code className="mono bg-white px-1 py-0.5">/api/partner-booking-key/frowen-fields</code>.
            It returns a stable signed URL you copy &amp; email to that partner once — they reuse it forever.
            Tell them: &ldquo;Bookmark this and pop in details every time a booking lands from CampCompass.&rdquo;
          </div>
        </details>
      </div>
    </div>
  );
}

function Cell({ label, value, hint, icon, border, accent }) {
  return (
    <div className={`p-3 ${border ? "border-l hairline" : ""} ${accent ? "bg-[#fff4ee]" : ""}`}>
      <div className="text-[10px] mono uppercase tracking-[0.18em] text-[#666] flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="font-display text-2xl mono mt-1">{value}</div>
      {hint && <div className="text-[10px] text-[#888] mt-1 leading-snug">{hint}</div>}
    </div>
  );
}
